import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ResponsiveBar } from '@nivo/bar';
import * as reportService from '../services/reportService';

export function VelocityReport(): React.ReactElement {
  const { token } = useAuth();
  const [reportData, setReportData] = useState<reportService.VelocityReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadReport() {
      if (!token) return;
      try {
        setLoading(true);
        const data = await reportService.getVelocityReport(token);
        setReportData(data);
      } catch (err: any) {
        console.error('Error loading velocity report:', err);
        setError('Failed to load velocity report');
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [token]);

  const chartData = useMemo(() => {
    if (!reportData) return [];
    return reportData.sprints.map(s => ({
      sprint: s.sprint_name,
      'Completed Points': s.completed_points,
      'Planned Points': s.planned_points,
      status: s.status,
    }));
  }, [reportData]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Velocity Report</h1>
          <p className="text-blue-100 mt-1">Track story points completed across sprints</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : reportData && reportData.sprints.length > 0 ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Average Velocity</p>
                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                  {reportData.average_velocity}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">pts / sprint</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Sprints</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {reportData.sprints.length}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Best Sprint</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {Math.max(...reportData.sprints.map(s => s.completed_points))}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">pts completed</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Latest Sprint</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {reportData.sprints[reportData.sprints.length - 1].completed_points}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">pts completed</p>
              </div>
            </div>

            {/* Velocity Bar Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sprint Velocity</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Story points completed per sprint</p>
              </div>
              <div className="px-6 py-4" style={{ height: 400 }}>
                <ResponsiveBar
                  data={chartData}
                  keys={['Completed Points', 'Planned Points']}
                  indexBy="sprint"
                  margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
                  padding={0.3}
                  groupMode="grouped"
                  colors={['#4f46e5', '#c7d2fe']}
                  borderRadius={4}
                  axisBottom={{
                    tickRotation: chartData.length > 6 ? -45 : 0,
                    legendOffset: 50,
                    legendPosition: 'middle' as const,
                  }}
                  axisLeft={{
                    legend: 'Story Points',
                    legendOffset: -40,
                    legendPosition: 'middle' as const,
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor="#ffffff"
                  tooltip={({ id, value, indexValue, color }) => (
                    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{indexValue}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-gray-700 dark:text-gray-300">{id}:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{value}</span>
                      </div>
                    </div>
                  )}
                  legends={[
                    {
                      dataFrom: 'keys' as const,
                      anchor: 'top-right' as const,
                      direction: 'row' as const,
                      translateY: -20,
                      itemWidth: 140,
                      itemHeight: 20,
                      symbolSize: 10,
                      symbolShape: 'circle' as const,
                    },
                  ]}
                  theme={{
                    axis: {
                      ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                      legend: { text: { fill: '#9ca3af', fontSize: 12 } },
                    },
                    grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                    legends: { text: { fill: '#6b7280', fontSize: 12 } },
                  }}
                  // Average velocity reference line via custom layer
                  layers={['grid', 'axes', 'bars', 'markers', 'legends', 'annotations',
                    ({ bars, yScale }: any) => {
                      const y = yScale(reportData.average_velocity);
                      if (bars.length === 0) return null;
                      const firstBar = bars[0];
                      const lastBar = bars[bars.length - 1];
                      return (
                        <g>
                          <line
                            x1={firstBar.x}
                            x2={lastBar.x + lastBar.width}
                            y1={y}
                            y2={y}
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="6,4"
                          />
                          <text
                            x={lastBar.x + lastBar.width + 4}
                            y={y + 4}
                            fill="#ef4444"
                            fontSize={11}
                            fontWeight={600}
                          >
                            Avg: {reportData.average_velocity}
                          </text>
                        </g>
                      );
                    }
                  ]}
                />
              </div>
            </div>

            {/* Sprint Details Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sprint Details</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sprint</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Items</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Planned Pts</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed Pts</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completion</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {reportData.sprints.map(sprint => (
                      <tr key={sprint.sprint_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                          {sprint.sprint_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            sprint.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            sprint.status === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            sprint.status === 'planning' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {sprint.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-100">
                          {sprint.completed_items} / {sprint.total_items}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-100">
                          {sprint.planned_points}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-semibold text-indigo-600 dark:text-indigo-400">
                          {sprint.completed_points}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          {sprint.planned_points > 0 ? (
                            <span className={`font-semibold ${
                              Math.round((sprint.completed_points / sprint.planned_points) * 100) >= 80
                                ? 'text-green-600 dark:text-green-400'
                                : Math.round((sprint.completed_points / sprint.planned_points) * 100) >= 50
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-red-600 dark:text-red-400'
                            }`}>
                              {Math.round((sprint.completed_points / sprint.planned_points) * 100)}%
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No sprint data yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Create and complete sprints to see velocity data.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default VelocityReport;
